import { type ReactNode, useState } from 'react';
import useDialogFocus from '@discover-ui/dialog/useDialogFocus';
import { useIsDesktop } from '@firsttable/web-theme';
import { cn } from '@discover-lib/utils';
import {
  DialogRoot,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@discover-ui/dialog';
import { PopoverRoot, PopoverContent, PopoverTrigger } from '@discover-ui/popover';
import ConditionalVisuallyHidden from '@discover-ui/conditionalVisuallyHidden';
import DropdownIcon from '@discover-ui/icons/dropdownIcon';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { type DialogTriggerProps } from '@discover-ui/dialog';
import dynamicDialogTriggerVariants, {
  type DynamicDialogTriggerVariantProps,
} from './triggerVariants';

const Label = ({ children }: { children: ReactNode }) => (
  <span className="overflow-hidden text-ellipsis text-nowrap">{children}</span>
);

export type DynamicDialogProps = {
  triggerLabel: ReactNode;
  triggerProps?: DynamicDialogTriggerVariantProps &
    Omit<DialogTriggerProps, 'role' | 'aria-expanded'>;
  dialogTitle: string;
  dialogTitleVisible?: boolean;
  dialogDescription: ReactNode;
  dialogDescriptionVisible?: boolean;
  contentClassName?: string;
  children: (props: { open: boolean; setOpen: (newOpen: boolean) => void }) => ReactNode;
};

export default function DynamicDialog({
  triggerLabel,
  triggerProps = {
    kind: 'outline',
    scale: 'md',
  },
  dialogTitle,
  dialogTitleVisible = true,
  dialogDescription,
  dialogDescriptionVisible = false,
  contentClassName,
  children,
}: DynamicDialogProps) {
  const {
    children: triggerChildren,
    className: triggerClassName,
    kind: triggerKind,
    round: triggerRound,
    scale: triggerScale,
    width: triggerWidth,
    ...otherTriggerProps
  } = triggerProps;
  const dialogFocus = useDialogFocus();
  const { track } = useAnalytics();
  const [open, setOpenModal] = useState(false);

  const setOpen = (value: boolean) => {
    track('button_clicked', {
      button_type: `${value ? 'open' : 'close'} modal`,
      button_name: 'toggle filter modal',
      value,
    });
    setOpenModal(value);
  };

  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <PopoverRoot open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          {...otherTriggerProps}
          role="combobox"
          aria-expanded={open}
          className={cn(
            dynamicDialogTriggerVariants({
              kind: triggerKind,
              scale: triggerScale,
              width: triggerWidth,
              round: triggerRound,
            }),
            triggerClassName
          )}>
          {triggerChildren}
          <Label>{triggerLabel}</Label>
          <DropdownIcon />
        </PopoverTrigger>
        <PopoverContent className={cn('min-w-36 p-0 pt-1', contentClassName)} align="start">
          {children({ open, setOpen })}
        </PopoverContent>
      </PopoverRoot>
    );
  }

  // mobile
  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger
        {...otherTriggerProps}
        role="combobox"
        aria-expanded={open}
        className={cn(
          dynamicDialogTriggerVariants({
            kind: triggerKind,
            scale: triggerScale,
            width: triggerWidth,
            round: triggerRound,
          }),
          triggerClassName
        )}>
        {triggerChildren}
        <Label>{triggerLabel}</Label>
        <DropdownIcon />
      </DialogTrigger>
      <DialogContent {...dialogFocus} className={contentClassName}>
        <ConditionalVisuallyHidden hidden={!dialogTitleVisible && !dialogDescriptionVisible}>
          <DialogHeader>
            <ConditionalVisuallyHidden hidden={!dialogTitleVisible}>
              <DialogTitle>{dialogTitle}</DialogTitle>
            </ConditionalVisuallyHidden>
            <ConditionalVisuallyHidden hidden={!dialogDescriptionVisible}>
              <DialogDescription>{dialogDescription}</DialogDescription>
            </ConditionalVisuallyHidden>
          </DialogHeader>
        </ConditionalVisuallyHidden>
        {children({ open, setOpen })}
      </DialogContent>
    </DialogRoot>
  );
}
